import { BusinessTripRate } from '@/app/models/configuration/businessTripRatesConfigurationModel'

export type SortKey =
  | 'id'
  | 'countryCode'
  | 'dateFrom'
  | 'dateTo'
  | 'allowanceRate'
  | 'allowanceSanitarityRate'
  | 'allowanceHotelLimitationRate'
  | 'allowanceAccomodationRate'

export const DEFAULT_SORT_KEY: SortKey = 'id'

export const PINNED_COUNTRY_CODES = ['PL', 'EU'] as const

export type SortState = {
  key: SortKey
  asc: boolean
}

export const DEFAULT_SORT_STATE: SortState = {
  key: DEFAULT_SORT_KEY,
  asc: true,
}

export const nextSortState = (current: SortState, key: SortKey): SortState => {
  if (current.key !== key) return { key, asc: true }
  if (current.asc) return { key, asc: false }
  return DEFAULT_SORT_STATE
}

const getTime = (value: unknown): number => {
  if (!value) return 0

  if (typeof (value as any).valueOf === 'function') {
    const parsed = Number((value as any).valueOf())
    if (Number.isFinite(parsed)) return parsed
  }

  const fromString = new Date(value as any).getTime()
  return Number.isFinite(fromString) ? fromString : 0
}

const getNumber = (value: unknown): number => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const getPinRank = (rate: BusinessTripRate): number => {
  const index = PINNED_COUNTRY_CODES.findIndex(
    (code) => rate.country?.code === code,
  )
  return index === -1 ? PINNED_COUNTRY_CODES.length : index
}

export const compareRates = (
  a: BusinessTripRate,
  b: BusinessTripRate,
  sortKey: SortKey,
  sortAsc: boolean,
): number => {
  if (Boolean(a.isNew) !== Boolean(b.isNew)) return a.isNew ? -1 : 1

  if (sortKey === DEFAULT_SORT_KEY) {
    const pinDiff = getPinRank(a) - getPinRank(b)
    if (pinDiff !== 0) return pinDiff
  }

  let diff: number

  switch (sortKey) {
    case 'id':
      diff = getNumber(a.id) - getNumber(b.id)
      break
    case 'countryCode':
      diff = (a.country?.name || '').localeCompare(b.country?.name || '')
      break
    case 'dateFrom':
      diff = getTime(a.dateFrom) - getTime(b.dateFrom)
      break
    case 'dateTo':
      diff = getTime(a.dateTo) - getTime(b.dateTo)
      break
    case 'allowanceRate':
      diff = getNumber(a.allowanceRate) - getNumber(b.allowanceRate)
      break
    case 'allowanceAccomodationRate':
      diff =
        getNumber(a.allowanceAccomodationRate) -
        getNumber(b.allowanceAccomodationRate)
      break
    case 'allowanceHotelLimitationRate':
      diff =
        getNumber(a.allowanceHotelLimitationRate) -
        getNumber(b.allowanceHotelLimitationRate)
      break
    case 'allowanceSanitarityRate':
      diff =
        getNumber(a.allowanceSanitarityRate) -
        getNumber(b.allowanceSanitarityRate)
      break
    default:
      return 0
  }

  if (diff === 0 && sortKey !== 'id') {
    return getNumber(a.id) - getNumber(b.id)
  }

  return sortAsc ? diff : -diff
}

export const sortRates = (
  rates: ReadonlyArray<BusinessTripRate>,
  sortKey: SortKey,
  sortAsc: boolean,
): BusinessTripRate[] =>
  [...rates].sort((a, b) => compareRates(a, b, sortKey, sortAsc))
